// AdminChatDashboard.jsx

import React, { useEffect, useState } from 'react';
import { db } from '../../modules/auth/firebaseConfig';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  onSnapshot,
  query,
  orderBy,
  addDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';

// ✅ WhatsApp Alert Function
const sendWhatsAppAlert = async (phoneNumber, message) => {
  const url = `https://api.callmebot.com/whatsapp.php?phone=${phoneNumber}&text=${encodeURIComponent(message)}&apikey=4550971`;
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to send WhatsApp alert');
  } catch (error) {
    console.error('WhatsApp error:', error);
  }
};

export default function AdminChatDashboard() {
  const [userList, setUserList] = useState([]);
  const [userMeta, setUserMeta] = useState({});
  const [unreadMap, setUnreadMap] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [sessionList, setSessionList] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newReply, setNewReply] = useState('');
  const [latestMessageTimeMap, setLatestMessageTimeMap] = useState({});

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'chats'), async (snapshot) => {
      const users = snapshot.docs.map(doc => doc.id);
      const meta = {};
      const unreadStatus = {};
      const latestTimeMap = {};

      for (let email of users) {
        const userRef = doc(db, 'chats', email);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          meta[email] = {
            name: data.name || '',
            city: data.city || '',
            country: data.country || ''
          };
        }

        const sessionsSnap = await getDocs(query(collection(db, 'chats', email, 'sessions')));
        let mostRecent = null;

        for (let sessionDoc of sessionsSnap.docs) {
          const sessionId = sessionDoc.id;
          const messagesSnap = await getDocs(
            query(collection(db, 'chats', email, 'sessions', sessionId, 'messages'), orderBy('timestamp', 'desc'))
          );

          if (!messagesSnap.empty) {
            const firstMsg = messagesSnap.docs[0];
            const ts = firstMsg.data()?.timestamp?.toMillis?.() || 0;
            if (!mostRecent || ts > mostRecent) {
              mostRecent = ts;
            }
            for (let msg of messagesSnap.docs) {
              if (msg.data().sender === 'user' && !msg.data().read) {
                if (!unreadStatus[email]) {
                  unreadStatus[email] = true;
                  const userName = meta[email]?.name || 'Unknown';
                  const userCity = meta[email]?.city || '';
                  const userCountry = meta[email]?.country || '';
                  const msgText = msg.data().text || '';
                  const alertMsg = `📩 New chat from ${userName} (${userCity}, ${userCountry}): ${msgText}`;
                  await sendWhatsAppAlert('+255789745684', alertMsg); // Update with your WhatsApp number and API key
                }
                break;
              }
            }
          }
        }

        latestTimeMap[email] = mostRecent || 0;
      }

      const sorted = users.sort((a, b) => {
        if (unreadStatus[b] && !unreadStatus[a]) return 1;
        if (!unreadStatus[b] && unreadStatus[a]) return -1;
        return (latestTimeMap[b] || 0) - (latestTimeMap[a] || 0);
      });

      setUserList(sorted);
      setUserMeta(meta);
      setUnreadMap(unreadStatus);
      setLatestMessageTimeMap(latestTimeMap);
    });

    return () => unsubscribe();
  }, []);

  // Section 3: Load sessions when a user is selected
  useEffect(() => {
    if (!selectedUser) return;
    const q = query(
      collection(db, 'chats', selectedUser, 'sessions'),
      orderBy('startedAt', 'desc')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setSessionList(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, [selectedUser]);

  // Section 4: Load messages when a session is selected
  useEffect(() => {
    if (!selectedUser || !selectedSession) return;
    const q = query(
      collection(db, 'chats', selectedUser, 'sessions', selectedSession, 'messages'),
      orderBy('timestamp', 'asc')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setMessages(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, [selectedUser, selectedSession]);

  // Section 5: Send reply and End Chat
  const sendReply = async () => {
    if (!newReply.trim() || !selectedUser || !selectedSession) return;
    try {
      await addDoc(
        collection(db, 'chats', selectedUser, 'sessions', selectedSession, 'messages'),
        {
          text: newReply.trim(),
          sender: 'admin',
          timestamp: serverTimestamp(),
          read: true,
        }
      );
      setNewReply('');
    } catch (error) {
      console.error('Error sending reply:', error);
    }
  };

  const endChat = async () => {
    if (!selectedUser || !selectedSession) return;
    try {
      await updateDoc(doc(db, 'chats', selectedUser, 'sessions', selectedSession), {
        endedAt: serverTimestamp(),
        endedBy: 'admin',
      });
      await addDoc(
        collection(db, 'chats', selectedUser, 'sessions', selectedSession, 'messages'),
        {
          text: '⚠️ This session has ended. Thank you!',
          sender: 'admin',
          timestamp: serverTimestamp(),
          read: true,
        }
      );
      alert("Please ask the user to rate the session.");
      setSelectedSession(null);
      setMessages([]);
    } catch (error) {
      console.error('Error ending session:', error);
    }
  };

  // Section 6: Render UI - Split Panel Layout
  return (
    <div className="flex w-full h-screen bg-black text-white">

      {/* Left Panel - Users */}
      <div className="w-1/5 p-4 border-r border-gray-700 overflow-y-auto">
        <h2 className="text-lg font-bold mb-4">📨 Users</h2>
{userList.map(email => {
  const lastTime = latestMessageTimeMap[email]
    ? new Date(latestMessageTimeMap[email]).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })
    : '—';

  return (
    <div
      key={email}
      onClick={() => {
        setSelectedUser(email);
        setSelectedSession(null);
        setMessages([]);
      }}
      className={`relative p-3 text-sm rounded-lg cursor-pointer mb-2 ${
        selectedUser === email ? 'bg-white text-black font-bold' : 'bg-gray-800 text-white hover:bg-gray-600'
      }`}
    >
      <div className="font-medium">{userMeta[email]?.name || 'Unknown'}</div>
      <div className="text-xs text-gray-300">{email}</div>
      <div className="text-xs text-gray-400">
        {userMeta[email]?.city}, {userMeta[email]?.country}
      </div>
      <div className="text-xs text-gray-400 mt-1">
        🕒 {lastTime}
      </div>
      {unreadMap[email] && (
        <div className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full"></div>
      )}
    </div>
  );
})}


      </div>

      {/* Center Panel - Sessions */}
      <div className="w-1/5 p-4 border-r border-gray-700 overflow-y-auto">
        <h2 className="text-lg font-bold mb-4">📁 Sessions</h2>
        {sessionList.map((session) => (
          <div
            key={session.id}
            onClick={() => setSelectedSession(session.id)}
            className={`p-3 text-sm rounded-lg cursor-pointer mb-2 ${
              selectedSession === session.id ? 'bg-white text-black font-bold' : 'bg-gray-800 text-white hover:bg-gray-600'
            }`}
          >
            {session.id.slice(0, 8)}... <br />
            <span className="text-xs text-gray-400 block">
  {session.city}, {session.country}
</span>
{session.startedAt && (
  <span className="text-xs text-yellow-400">
    {new Date(session.startedAt?.toDate?.()).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    })}
  </span>
)}

          </div>
        ))}
      </div>

      {/* Right Panel - Chat Box */}
      <div className="flex-1 flex flex-col p-4">
        <h2 className="text-lg font-bold mb-2">
          Chat with: <span className="text-yellow-400">{userMeta[selectedUser]?.name}</span>
        </h2>

        {selectedUser && selectedSession && (
          <div className="flex justify-end mb-2">
            <button
              onClick={endChat}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
            >
              End Chat
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto bg-gray-900 p-4 rounded-md">
{messages.map(msg => {
  const timeStr = msg.timestamp?.toDate
    ? new Date(msg.timestamp.toDate()).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })
    : '';
  
  return (
    <div
      key={msg.id}
      className={`p-2 mb-2 rounded-md max-w-md text-sm ${
        msg.sender === 'admin' ? 'bg-green-700 ml-auto text-right' : 'bg-gray-700 text-left'
      }`}
    >
      <div>{msg.text}</div>
      {timeStr && (
        <div className="text-xs text-gray-300 mt-1">
          {timeStr}
        </div>
      )}
    </div>
  );
})}

        </div>

        <div className="mt-4 flex">
          <input
            type="text"
            value={newReply}
            onChange={(e) => setNewReply(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendReply()}
            placeholder="Type your reply..."
            className="flex-1 p-2 rounded-md text-black"
          />
          <button
            onClick={sendReply}
            className="ml-2 px-4 py-2 bg-yellow-500 text-black rounded-md"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
